import {
  apiFetch,
  apiPatch,
  apiPut,
  apiPost,
  apiDelete,
} from '@/utils/fetchWrapper';
import {
  appendPaginationParams,
  buildCallHistoryQueryString,
  buildPaginatedPath,
  buildUsersQueryString,
} from '@/utils/queryParams';
import { User, CreateUserRequest, UserUpdatePayload } from '@/types/api';
import { CallHistoryFilters, PaginatedCallsResponse } from '@/types/calls';
import {
  UsersFilters,
  NominalContact,
  CreateContactRequest,
  UpdateContactRequest,
  SharedContact,
} from '@/types/nominals';

export const userService = {
  async getCurrentUser(): Promise<User> {
    const data = await apiFetch<User & { pk?: string }>('/auth/me/');
    return {
      ...data,
      id: data.id ?? data.pk ?? '',
    };
  },

  async getUser(userId: string): Promise<User> {
    return apiFetch<User>(`/users/${userId}/`);
  },

  async updateUser(userId: string, updates: UserUpdatePayload): Promise<User> {
    try {
      return await apiPatch<User, UserUpdatePayload>(`/users/${userId}/`, updates);
    } catch (error) {
      // Fallback to PUT if PATCH fails
      return await apiPut<User, UserUpdatePayload>(`/users/${userId}/`, updates);
    }
  },

  async getUserCallHistory(
    filters: CallHistoryFilters = {}
  ): Promise<PaginatedCallsResponse> {
    const url = buildPaginatedPath(
      '/user_call/',
      buildCallHistoryQueryString(filters)
    );
    return await apiFetch<PaginatedCallsResponse>(url);
  },

  async getUserCallHistoryWithFilters(
    userId: string,
    filters: CallHistoryFilters = {}
  ): Promise<PaginatedCallsResponse> {
    const url = buildPaginatedPath(
      '/user_call/',
      buildCallHistoryQueryString(filters, { user: userId })
    );
    return await apiFetch<PaginatedCallsResponse>(url);
  },

  async getUsers(filters: UsersFilters = {}): Promise<{
    count: number;
    results: User[];
    next?: string | null;
    previous?: string | null;
  }> {
    const url = buildPaginatedPath('/users/', buildUsersQueryString(filters));
    return apiFetch(url);
  },

  async createUser(userData: CreateUserRequest): Promise<User> {
    return apiPost<User, CreateUserRequest>('/users/', userData);
  },

  async getUserContacts(
    userId: string,
    filters: { page?: number; page_size?: number } = {}
  ): Promise<{
    count: number;
    results: NominalContact[];
    next?: string | null;
    previous?: string | null;
  }> {
    const params = new URLSearchParams();
    appendPaginationParams(params, filters);
    const url = buildPaginatedPath(`/users/${userId}/contacts/`, params.toString());

    return apiFetch<{
      count: number;
      results: NominalContact[];
      next?: string | null;
      previous?: string | null;
    }>(url);
  },

  async createUserContact(
    userId: string,
    contact: CreateContactRequest
  ): Promise<NominalContact> {
    return await apiPost<NominalContact, CreateContactRequest>(
      `/users/${userId}/contacts/`,
      contact
    );
  },

  async updateUserContact(
    userId: string,
    contactId: string,
    updates: UpdateContactRequest
  ): Promise<NominalContact> {
    return await apiPut<NominalContact, UpdateContactRequest>(
      `/users/${userId}/contacts/${contactId}/`,
      updates
    );
  },

  async deleteUserContact(userId: string, contactId: string): Promise<void> {
    await apiDelete(`/users/${userId}/contacts/${contactId}/`);
  },

  async getSharedContacts(
    filters: { search?: string; page?: number; page_size?: number } = {}
  ): Promise<{
    count: number;
    results: SharedContact[];
    next?: string | null;
    previous?: string | null;
  }> {
    const params = new URLSearchParams();
    appendPaginationParams(params, filters);
    if (filters.search) {
      params.append('search', filters.search);
    }
    const url = buildPaginatedPath('/shared_contacts/', params.toString());

    return apiFetch<{
      count: number;
      results: SharedContact[];
      next?: string | null;
      previous?: string | null;
    }>(url);
  },

  async createSharedContact(contact: {
    phone_number: string;
    first_name: string;
    last_name: string;
    contact_type: string;
    establishment_id?: number;
    country_code?: string;
  }): Promise<SharedContact> {
    return await apiPost<SharedContact, typeof contact>(
      '/shared_contacts/',
      contact
    );
  },

  async updateSharedContact(
    contactId: string,
    contact: {
      phone_number: string;
      first_name: string;
      last_name: string;
      contact_type: string;
      country_code?: string;
    }
  ): Promise<SharedContact> {
    return await apiPut<SharedContact, typeof contact>(
      `/shared_contacts/${contactId}/`,
      contact
    );
  },

  async deleteSharedContact(contactId: string): Promise<void> {
    await apiDelete(`/shared_contacts/${contactId}/`);
  },
};
